import { Entity, EntityProfileIndustry } from "@common/types/entity";
import {
    ConsolidationGroupResponse,
    ConsolidationRelationshipResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import {
    ConsolidationEntityRelationshipType,
    ConsolidationTreatment,
} from "@common/types/domains/accounting/consolidation";
import { ENTITY_TYPE_LABELS } from "@components/settings/EntitySettings/entityTypeLabels";
import { industryLabels } from "@components/entity/constants";
import {
    ENTITY_NODE_HEIGHT,
    ENTITY_NODE_WIDTH,
    OwnershipEntityKindFilter,
    OwnershipFlowEdge,
    OwnershipFlowNode,
    OwnershipSelection,
    RelationshipFilter,
} from "./ownership.types";

export const FULL_OWNERSHIP_PERCENTAGE = 100;

export function getOwnershipEntityDisplayName(entity: {
    isPersonal: boolean;
    name: string | null;
    user?: { name: string } | null;
}): string {
    return entity.isPersonal && entity.user?.name
        ? entity.user.name
        : (entity.name ?? "Unnamed entity");
}

export function getOwnershipEntityIndustry(entity: Entity): string | null {
    let industries: EntityProfileIndustry[] = [];
    if (entity.profile?.industries && entity.profile.industries.length > 0) {
        industries = entity.profile.industries;
    } else if (entity.profile?.industry) {
        industries = [entity.profile.industry];
    }
    if (industries.length === 0) {
        return null;
    }
    return industries
        .map((industry) =>
            industry === EntityProfileIndustry.OTHER &&
            entity.profile?.industryOther
                ? entity.profile.industryOther
                : industryLabels[industry],
        )
        .join(", ");
}

export const RELATIONSHIP_TYPE_LABELS: Record<
    ConsolidationEntityRelationshipType,
    string
> = {
    [ConsolidationEntityRelationshipType.ownership]: "Equity",
    [ConsolidationEntityRelationshipType.loan]: "Debt",
    [ConsolidationEntityRelationshipType.rent]: "Transaction",
    [ConsolidationEntityRelationshipType.cashSweep]: "Transaction",
    [ConsolidationEntityRelationshipType.managementFee]: "Transaction",
    [ConsolidationEntityRelationshipType.sharedExpenses]: "Transaction",
    [ConsolidationEntityRelationshipType.other]: "Transaction",
};

export const RELATIONSHIP_PARTY_LABELS: Record<
    ConsolidationEntityRelationshipType,
    { source: string; target: string }
> = {
    [ConsolidationEntityRelationshipType.ownership]: {
        source: "Parent",
        target: "Child",
    },
    [ConsolidationEntityRelationshipType.loan]: {
        source: "Lender",
        target: "Borrower",
    },
    [ConsolidationEntityRelationshipType.rent]: {
        source: "Payer",
        target: "Payee",
    },
    [ConsolidationEntityRelationshipType.cashSweep]: {
        source: "Payer",
        target: "Payee",
    },
    [ConsolidationEntityRelationshipType.managementFee]: {
        source: "Payer",
        target: "Payee",
    },
    [ConsolidationEntityRelationshipType.sharedExpenses]: {
        source: "Payer",
        target: "Payee",
    },
    [ConsolidationEntityRelationshipType.other]: {
        source: "Payer",
        target: "Payee",
    },
};

export const RELATIONSHIP_FILTER_LABELS: Record<RelationshipFilter, string> = {
    all: "All relationships",
    [ConsolidationEntityRelationshipType.ownership]: "Equity",
    [ConsolidationEntityRelationshipType.loan]: "Debt",
    [ConsolidationEntityRelationshipType.other]: "Transaction",
};

export function groupNodeId(groupId: string): string {
    return `group:${groupId}`;
}

export function getEntitySubtitle(entity: Entity): string {
    if (entity.isPersonal) {
        return "Owner";
    }
    if (entity.legalType) {
        return ENTITY_TYPE_LABELS[entity.legalType];
    }
    return "Business";
}

function entityMatchesSearch(
    entity: Entity,
    normalizedSearch: string,
): boolean {
    if (!normalizedSearch) {
        return true;
    }
    return [
        entity.name,
        entity.user?.name,
        entity.user?.preferredName,
        entity.user?.email,
        getEntitySubtitle(entity),
    ].some((value) => value?.toLowerCase().includes(normalizedSearch));
}

export function isActiveRelationship(
    relationship: ConsolidationRelationshipResponse,
): boolean {
    return relationship.endDate === null;
}

function isActiveFullOwnershipFrom({
    relationship,
    parentEntityUuid,
}: {
    relationship: ConsolidationRelationshipResponse;
    parentEntityUuid: string;
}): boolean {
    return (
        isActiveRelationship(relationship) &&
        relationship.type === ConsolidationEntityRelationshipType.ownership &&
        relationship.sourceEntityUuid === parentEntityUuid &&
        relationship.ownershipPercentage === FULL_OWNERSHIP_PERCENTAGE
    );
}

interface WhollyOwnedBusinessSubsidiariesInput {
    parentEntityUuid: string;
    relationships: ConsolidationRelationshipResponse[];
    entitiesByUuid: Map<string, Entity>;
}

export function getWhollyOwnedBusinessSubsidiaryUuids({
    parentEntityUuid,
    relationships,
    entitiesByUuid,
}: WhollyOwnedBusinessSubsidiariesInput): string[] {
    return [
        ...new Set(
            relationships
                .filter(
                    (relationship) =>
                        isActiveFullOwnershipFrom({
                            relationship,
                            parentEntityUuid,
                        }) &&
                        entitiesByUuid.get(relationship.targetEntityUuid)
                            ?.isBusiness === true,
                )
                .map((relationship) => relationship.targetEntityUuid),
        ),
    ];
}

export function getEdgeLabel(
    relationship: ConsolidationRelationshipResponse,
): string {
    if (
        relationship.type === ConsolidationEntityRelationshipType.ownership &&
        relationship.ownershipPercentage !== null
    ) {
        return `${relationship.ownershipPercentage}%`;
    }
    return (
        relationship.metadata?.name ??
        RELATIONSHIP_TYPE_LABELS[relationship.type]
    );
}

/**
 * Label shown on a collapsed bundle of relationships between an entity pair.
 */
export function getBundleLabel(count: number): string {
    return `${count} relationships`;
}

export function unorderedPairKey(a: string, b: string): string {
    return a < b ? `${a}__${b}` : `${b}__${a}`;
}

/**
 * Maps an edge to the selection it represents when clicked: a multi-member
 * bundle selects the whole pair (so the side panel lists every relationship);
 * any other edge selects its single relationship.
 */
export function selectionForEdge(
    edge: Pick<OwnershipFlowEdge, "id" | "source" | "target" | "data">,
): OwnershipSelection {
    const bundled = edge.data?.bundledRelationships ?? [];
    if (bundled.length > 1) {
        return {
            kind: "relationshipBundle",
            sourceEntityUuid: edge.source,
            targetEntityUuid: edge.target,
        };
    }
    return { kind: "relationship", relationshipId: edge.id };
}

/**
 * Collapses every reference relationship between a pair of entities — in EITHER
 * direction — into a single bundle edge, so the canvas never stacks more
 * lines/labels between two nodes than the geometry allows. The bundle carries
 * all member relationships (used for the count label, the bidirectional arrow,
 * and the side-panel breakdown). Ownership edges always pass through untouched.
 */
export function bundleReferenceEdges(
    edges: OwnershipFlowEdge[],
): OwnershipFlowEdge[] {
    const passthrough: OwnershipFlowEdge[] = [];
    const groupsByPair = new Map<
        string,
        {
            representative: OwnershipFlowEdge;
            members: OwnershipFlowEdge[];
            relationships: ConsolidationRelationshipResponse[];
        }
    >();

    for (const edge of edges) {
        const data = edge.data;
        if (
            !data ||
            data.relationship.type ===
                ConsolidationEntityRelationshipType.ownership
        ) {
            passthrough.push(edge);
            continue;
        }
        const key = unorderedPairKey(edge.source, edge.target);
        const group = groupsByPair.get(key);
        if (group) {
            group.members.push(edge);
            group.relationships.push(data.relationship);
        } else {
            groupsByPair.set(key, {
                representative: edge,
                members: [edge],
                relationships: [data.relationship],
            });
        }
    }

    const bundled: OwnershipFlowEdge[] = [];
    for (const [key, group] of groupsByPair) {
        const data = group.representative.data;
        if (group.relationships.length === 1 || !data) {
            bundled.push(...group.members);
            continue;
        }
        bundled.push({
            ...group.representative,
            id: `bundle:${key}`,
            data: { ...data, bundledRelationships: group.relationships },
        });
    }

    return [...passthrough, ...bundled];
}

/**
 * Members render inside the group boundary only when their parent edge is
 * exactly 100% (AC 1.1) — `<100%` members stay visible outside the boundary.
 */
export function getBoundaryEntityUuids({
    group,
    relationships,
}: {
    group: ConsolidationGroupResponse;
    relationships: ConsolidationRelationshipResponse[];
}): Set<string> {
    const fullyOwnedTargets = new Set(
        relationships
            .filter((relationship) =>
                isActiveFullOwnershipFrom({
                    relationship,
                    parentEntityUuid: group.parentEntityUuid,
                }),
            )
            .map((relationship) => relationship.targetEntityUuid),
    );

    const boundaryUuids = new Set<string>([group.parentEntityUuid]);
    for (const member of group.members) {
        if (fullyOwnedTargets.has(member.entityUuid)) {
            boundaryUuids.add(member.entityUuid);
        }
    }
    return boundaryUuids;
}

interface BuildOwnershipGraphParams {
    entities: Entity[];
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
    search?: string;
    relationshipType?: Exclude<RelationshipFilter, "all">;
    entityKind?: OwnershipEntityKindFilter;
}

interface OwnershipGraph {
    nodes: OwnershipFlowNode[];
    edges: OwnershipFlowEdge[];
}

function matchesRelationshipFilter({
    type,
    relationshipType,
}: {
    type: string;
    relationshipType: Exclude<RelationshipFilter, "all"> | undefined;
}): boolean {
    if (!relationshipType) {
        return true;
    }
    if (relationshipType === ConsolidationEntityRelationshipType.other) {
        return (
            type !== ConsolidationEntityRelationshipType.ownership &&
            type !== ConsolidationEntityRelationshipType.loan
        );
    }
    return type === relationshipType;
}

export function getVisibleLayoutedNodes({
    visibleNodes,
    layoutedNodes,
}: {
    visibleNodes: OwnershipFlowNode[];
    layoutedNodes: OwnershipFlowNode[];
}): OwnershipFlowNode[] {
    const visibleNodeIds = new Set(visibleNodes.map(({ id }) => id));
    for (const node of layoutedNodes) {
        if (visibleNodeIds.has(node.id) && node.parentId) {
            visibleNodeIds.add(node.parentId);
        }
    }
    return layoutedNodes.filter(({ id }) => visibleNodeIds.has(id));
}

export function buildOwnershipGraph({
    entities,
    relationships,
    groups,
    search,
    relationshipType,
    entityKind = "all",
}: BuildOwnershipGraphParams): OwnershipGraph {
    const normalizedSearch = search?.trim().toLowerCase() ?? "";

    const visibleEntities = entities.filter((entity) => {
        if (entity.isMockEntity) {
            return false;
        }
        if (entityKind === "business" && !entity.isBusiness) {
            return false;
        }
        if (entityKind === "personal" && !entity.isPersonal) {
            return false;
        }
        if (!entityMatchesSearch(entity, normalizedSearch)) {
            return false;
        }
        return true;
    });
    const visibleUuids = new Set(visibleEntities.map(({ uuid }) => uuid));

    const treatmentByEntityUuid = new Map<string, ConsolidationTreatment>();
    for (const group of groups) {
        for (const member of group.members) {
            treatmentByEntityUuid.set(
                member.entityUuid,
                member.consolidationTreatment,
            );
        }
    }

    const nodes: OwnershipFlowNode[] = [];

    const boundaryByGroupId = new Map(
        groups.map((group) => [
            group.id,
            getBoundaryEntityUuids({ group, relationships }),
        ]),
    );

    for (const group of groups) {
        const boundaryUuids =
            boundaryByGroupId.get(group.id) ?? new Set<string>();
        const visibleBoundaryUuids = [...boundaryUuids].filter((uuid) =>
            visibleUuids.has(uuid),
        );
        // A boundary without its parent (filtered out) would render a
        // confusing partial group box.
        if (
            visibleBoundaryUuids.length === 0 ||
            !visibleUuids.has(group.parentEntityUuid)
        ) {
            continue;
        }
        nodes.push({
            id: groupNodeId(group.id),
            type: "groupNode",
            position: { x: 0, y: 0 },
            // Pin the boundary below entity nodes so React Flow does not
            // auto-elevate grouped edges above the edge-label layer (which
            // would render ownership labels under their edges).
            zIndex: 0,
            data: {
                group,
                memberCount: visibleBoundaryUuids.length,
            },
        });
    }
    const groupNodeIds = new Set(nodes.map(({ id }) => id));

    for (const entity of visibleEntities) {
        // An entity can sit in two boundaries (member of one group while
        // being the parent of another); membership wins deterministically.
        const boundaryGroups = groups.filter(
            (group) =>
                groupNodeIds.has(groupNodeId(group.id)) &&
                boundaryByGroupId.get(group.id)?.has(entity.uuid),
        );
        const owningGroup =
            boundaryGroups.find(({ members }) =>
                members.some(({ entityUuid }) => entityUuid === entity.uuid),
            ) ?? boundaryGroups[0];

        nodes.push({
            id: entity.uuid,
            type: "entityNode",
            position: { x: 0, y: 0 },
            width: ENTITY_NODE_WIDTH,
            height: ENTITY_NODE_HEIGHT,
            zIndex: 1,
            ...(owningGroup
                ? {
                      parentId: groupNodeId(owningGroup.id),
                      extent: "parent" as const,
                  }
                : {}),
            data: {
                entity,
                subtitle: getEntitySubtitle(entity),
                treatment: treatmentByEntityUuid.get(entity.uuid) ?? null,
            },
        });
    }

    const edges: OwnershipFlowEdge[] = relationships
        .filter(
            (relationship) =>
                isActiveRelationship(relationship) &&
                matchesRelationshipFilter({
                    type: relationship.type,
                    relationshipType,
                }) &&
                visibleUuids.has(relationship.sourceEntityUuid) &&
                visibleUuids.has(relationship.targetEntityUuid),
        )
        .map((relationship) => ({
            id: relationship.id,
            type: "ownershipEdge",
            source: relationship.sourceEntityUuid,
            target: relationship.targetEntityUuid,
            data: { relationship },
        }));

    return { nodes, edges };
}
